import type { VisualStyle } from './visualStyles.js';

/**
 * Per-style continuous animation for a CONNECTED pair — the line overlay and
 * the endpoint-dot overlay. Every visual style gets its own motion (pipe =
 * flowing light, fire = rising embers, lightning = flickering bolt, ...), all
 * drawn as a pure overlay on top of the already-rendered tube/dot so none of
 * the per-style segment/dot functions need to know about it. `gear` is not
 * handled here — it has its own chain-spin.
 */

export interface Pt {
  x: number;
  y: number;
}

interface Sample {
  x: number;
  y: number;
  tx: number;
  ty: number;
  nx: number;
  ny: number;
}

const TAU = Math.PI * 2;
const fract = (x: number): number => x - Math.floor(x);
const rnd = (n: number): number => fract(Math.sin(n * 127.1 + 311.7) * 43758.5453);
/** Gaussian bump on a 0..1 phase — used for heartbeat-style pulses. */
const bump = (ph: number, center: number, width: number): number =>
  Math.exp(-(((ph - center) / width) ** 2));

class PathGeom {
  readonly total: number;
  private readonly cum: number[] = [0];

  constructor(readonly pts: Pt[]) {
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1]!;
      const b = pts[i]!;
      this.cum.push(this.cum[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y));
    }
    this.total = this.cum[this.cum.length - 1]! || 1;
  }

  at(s: number, wrap = true): Sample {
    const L = this.total;
    const d = wrap ? ((s % L) + L) % L : Math.min(L, Math.max(0, s));
    let i = 1;
    while (i < this.cum.length - 1 && this.cum[i]! < d) i++;
    const a = this.pts[i - 1]!;
    const b = this.pts[i]!;
    const segLen = this.cum[i]! - this.cum[i - 1]! || 1;
    const u = (d - this.cum[i - 1]!) / segLen;
    const tx = (b.x - a.x) / segLen;
    const ty = (b.y - a.y) / segLen;
    return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, tx, ty, nx: -ty, ny: tx };
  }
}

interface Env {
  ctx: CanvasRenderingContext2D;
  g: PathGeom;
  /** cell size */
  c: number;
  /** half tube width */
  w: number;
  /** seconds, already phase-shifted per pair */
  t: number;
  base: string;
  light: string;
  seedInt: number;
}

type FxFn = (e: Env) => void;

function tracePts(ctx: CanvasRenderingContext2D, pts: Pt[]): void {
  ctx.beginPath();
  pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
}

function sampled(g: PathGeom, step: number, off?: (s: number) => number): Pt[] {
  const out: Pt[] = [];
  const n = Math.max(2, Math.ceil(g.total / step));
  for (let i = 0; i <= n; i++) {
    const s = (i / n) * g.total;
    const m = g.at(s, false);
    const o = off ? off(s) : 0;
    out.push({ x: m.x + m.nx * o, y: m.y + m.ny * o });
  }
  return out;
}

function disc(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.1, r), 0, TAU);
  ctx.fill();
}

function ring(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, Math.max(0.1, r), 0, TAU);
  ctx.stroke();
}

function star(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  const k = r * 0.22;
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x + k, y - k);
  ctx.lineTo(x + r, y);
  ctx.lineTo(x + k, y + k);
  ctx.lineTo(x, y + r);
  ctx.lineTo(x - k, y + k);
  ctx.lineTo(x - r, y);
  ctx.lineTo(x - k, y - k);
  ctx.closePath();
  ctx.fill();
}

function oval(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  rot: number,
): void {
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0.1, rx), Math.max(0.1, ry), rot, 0, TAU);
  ctx.fill();
}

function glow(ctx: CanvasRenderingContext2D, color: string, blur: number): void {
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
}

/** Number of "spawn slots" for particle-style effects, scaled by path length. */
function slots(e: Env, perCell: number): number {
  return Math.max(3, Math.round((e.g.total / e.c) * perCell));
}

/** A tapered bright band of slanted ticks sweeping over the tube (metal glint). */
function glint(
  e: Env,
  head: number,
  len: number,
  color: string,
  peak: number,
  slant: number,
  lineW: number,
): void {
  const { ctx, g, w } = e;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineW;
  ctx.lineCap = 'butt';
  const step = len / 14;
  for (let u = 0; u <= len; u += step) {
    const s = head - len + u;
    if (s < 0 || s > g.total) continue;
    const m = g.at(s, false);
    ctx.globalAlpha = Math.sin((Math.PI * u) / len) * peak;
    ctx.beginPath();
    ctx.moveTo(m.x - m.nx * w * 0.95 - m.tx * w * slant, m.y - m.ny * w * 0.95 - m.ty * w * slant);
    ctx.lineTo(m.x + m.nx * w * 0.95 + m.tx * w * slant, m.y + m.ny * w * 0.95 + m.ty * w * slant);
    ctx.stroke();
  }
}

/** Diagonal stripes crawling along the tube (rope twist / candy cane). */
function stripes(
  e: Env,
  spacing: number,
  speed: number,
  slant: number,
  color: string,
  lineW: number,
  alpha: number,
  cap: CanvasLineCap,
): void {
  const { ctx, g, w, t } = e;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineW;
  ctx.lineCap = cap;
  ctx.globalAlpha = alpha;
  const shift = fract((t * speed) / spacing) * spacing;
  for (let s = shift; s < g.total; s += spacing) {
    const m = g.at(s, false);
    ctx.beginPath();
    ctx.moveTo(m.x - m.nx * w * 0.9 - m.tx * w * slant, m.y - m.ny * w * 0.9 - m.ty * w * slant);
    ctx.lineTo(m.x + m.nx * w * 0.9 + m.tx * w * slant, m.y + m.ny * w * 0.9 + m.ty * w * slant);
    ctx.stroke();
  }
}

const fxPipe: FxFn = ({ ctx, g, c, w, t, light }) => {
  tracePts(ctx, g.pts);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash([c * 0.35, c * 0.75]);
  ctx.lineDashOffset = -t * c * 2.4;
  ctx.strokeStyle = '#ffffff';
  ctx.globalAlpha = 0.9;
  ctx.lineWidth = w * 0.9;
  glow(ctx, light, c * 0.35);
  ctx.stroke();
};

const fxSnake: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  ctx.fillStyle = light;
  glow(ctx, light, c * 0.2);
  for (let s = 0; s <= g.total; s += c * 0.1) {
    const m = g.at(s, false);
    const k = 0.5 + 0.5 * Math.sin((s / c) * 2.6 - t * 5.5);
    ctx.globalAlpha = 0.25 + 0.65 * k ** 3;
    disc(ctx, m.x, m.y, w * (0.45 + 0.75 * k ** 3));
  }
};

const fxChain: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  ctx.fillStyle = '#ffffff';
  glow(ctx, e.light, c * 0.25);
  const n = Math.floor(g.total / (c * 0.5));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.5, false);
    const b = Math.max(0, Math.sin(i * 0.9 - t * 7));
    ctx.globalAlpha = b ** 2 * 0.95;
    oval(ctx, m.x, m.y, w * 1.0 * (0.5 + b * 0.5), w * 0.45, Math.atan2(m.ty, m.tx));
  }
};

const fxRope: FxFn = (e) => {
  stripes(e, e.c * 0.2, e.c * 0.7, 0.45, 'rgba(0,0,0,0.5)', Math.max(2, e.c * 0.05), 1, 'butt');
  stripes(e, e.c * 0.2, e.c * 0.7, 0.45, 'rgba(255,255,255,0.75)', Math.max(1.5, e.c * 0.03), 1, 'butt');
};

const fxWave: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  glow(ctx, light, c * 0.2);
  tracePts(ctx, sampled(g, c * 0.08, (s) => Math.sin((s / c) * 5.5 - t * 6) * w * 0.65));
  ctx.strokeStyle = '#ffffff';
  ctx.globalAlpha = 0.85;
  ctx.lineWidth = Math.max(2, w * 0.4);
  ctx.stroke();
  tracePts(ctx, sampled(g, c * 0.08, (s) => Math.sin((s / c) * 5.5 - t * 6 + Math.PI) * w * 0.65));
  ctx.globalAlpha = 0.45;
  ctx.stroke();
};

const fxCrystal: FxFn = (e) => {
  const { ctx, g, c, t, seedInt } = e;
  ctx.fillStyle = '#ffffff';
  glow(ctx, e.light, c * 0.3);
  const n = slots(e, 1.4);
  for (let i = 0; i < n; i++) {
    const ph = fract(t * 0.75 + rnd(seedInt + i * 3));
    const a = Math.sin(Math.PI * ph) ** 2;
    const m = g.at(rnd(seedInt + i) * g.total, false);
    ctx.globalAlpha = a;
    star(ctx, m.x, m.y, c * 0.3 * a);
  }
  glint(e, fract(t * 0.5) * (g.total + c * 2), c * 1.4, '#ffffff', 0.55, 0.7, Math.max(2, c * 0.07));
};

const fxLightning: FxFn = (e) => {
  const { ctx, g, c, w, t, light, seedInt } = e;
  const k = Math.floor(t * 12);
  const flick = rnd(seedInt + k * 7) > 0.22 ? 1 : 0.25;
  const pts = sampled(g, c * 0.22);
  const jag = pts.map((p, i) => {
    if (i === 0 || i === pts.length - 1) return p;
    const r = rnd(seedInt + k * 131 + i) - 0.5;
    const s = (i / (pts.length - 1)) * g.total;
    const m = g.at(s, false);
    return { x: p.x + m.nx * r * w * 2.2, y: p.y + m.ny * r * w * 2.2 };
  });
  ctx.lineJoin = 'miter';
  ctx.lineCap = 'round';
  tracePts(ctx, jag);
  glow(ctx, light, c * 0.5);
  ctx.strokeStyle = light;
  ctx.globalAlpha = 0.6 * flick;
  ctx.lineWidth = Math.max(4, w * 0.8);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.globalAlpha = flick;
  ctx.lineWidth = Math.max(1.5, w * 0.3);
  ctx.stroke();
};

const fxSpring: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  ctx.strokeStyle = '#ffffff';
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(2.5, c * 0.06);
  const n = Math.floor(g.total / (c * 0.26));
  for (let i = 0; i <= n; i++) {
    const s = i * c * 0.26 + Math.sin(t * 4.5 - i * 0.55) * c * 0.08;
    const m = g.at(s, false);
    ctx.globalAlpha = 0.45 + 0.4 * (0.5 + 0.5 * Math.sin(t * 4.5 - i * 0.55));
    ctx.beginPath();
    ctx.moveTo(m.x - m.nx * w * 0.95, m.y - m.ny * w * 0.95);
    ctx.lineTo(m.x + m.nx * w * 0.95, m.y + m.ny * w * 0.95);
    ctx.stroke();
  }
};

const fxThread: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  const head = (t * c * 3.2) % (g.total + c);
  glow(ctx, light, c * 0.3);
  for (let k = 0; k < 14; k++) {
    const m = g.at(head - k * c * 0.09, false);
    if (head - k * c * 0.09 < 0) continue;
    ctx.fillStyle = k === 0 ? '#ffffff' : light;
    ctx.globalAlpha = (1 - k / 14) * 0.95;
    disc(ctx, m.x, m.y, w * 0.55 * (1 - k / 20));
  }
};

const fxBubble: FxFn = (e) => {
  const { ctx, g, c, t, seedInt } = e;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(2, c * 0.045);
  const n = slots(e, 1.6);
  for (let i = 0; i < n; i++) {
    const life = fract(t * 0.45 + rnd(seedInt + i * 5));
    const m = g.at(rnd(seedInt + i) * g.total, false);
    const r = c * (0.09 + 0.09 * rnd(seedInt + i * 9));
    const x = m.x + Math.sin(life * 9 + i) * c * 0.06;
    const y = m.y - life * c * 0.42;
    ctx.globalAlpha = (1 - life) * 0.95;
    ring(ctx, x, y, r);
    ctx.fillStyle = '#ffffff';
    disc(ctx, x - r * 0.35, y - r * 0.35, r * 0.25);
  }
};

const fxRibbon: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  const len = c * 1.8;
  const head = (t * c * 1.7) % (g.total + len);
  ctx.lineCap = 'round';
  glow(ctx, light, c * 0.2);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(2, w * 0.55);
  let prev: Pt | null = null;
  for (let u = 0; u <= len; u += c * 0.08) {
    const s = head - len + u;
    if (s < 0 || s > g.total) {
      prev = null;
      continue;
    }
    const m = g.at(s, false);
    const o = Math.sin((s / c) * 3.2 - t * 4.5) * w * 0.5;
    const p = { x: m.x + m.nx * o, y: m.y + m.ny * o };
    if (prev) {
      ctx.globalAlpha = Math.sin((Math.PI * u) / len) * 0.9;
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    prev = p;
  }
};

const fxFire: FxFn = (e) => {
  const { ctx, g, c, t, seedInt, light } = e;
  const n = slots(e, 2.2);
  for (let i = 0; i < n; i++) {
    const life = fract(t * 1.1 + rnd(seedInt + i * 5));
    const m = g.at(rnd(seedInt + i) * g.total, false);
    const x = m.x + Math.sin(life * 11 + i * 2) * c * 0.08;
    const y = m.y - life * c * 0.45;
    ctx.fillStyle = life < 0.35 ? '#fff3a0' : life < 0.65 ? '#ffb02e' : light;
    glow(ctx, '#ff7a1a', c * 0.25);
    ctx.globalAlpha = (1 - life) * 0.95;
    oval(ctx, x, y, c * 0.11 * (1 - life * 0.6), c * 0.2 * (1 - life * 0.5), 0);
  }
};

const fxVine: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  glow(ctx, '#2f8f1f', c * 0.12);
  const n = Math.floor(g.total / (c * 0.65));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.65, false);
    const side = i % 2 === 0 ? 1 : -1;
    const k = 0.5 + 0.5 * Math.sin(t * 2.6 - i * 0.9);
    const sway = Math.sin(t * 3 + i) * 0.25;
    const rot = Math.atan2(m.ny * side, m.nx * side) + sway;
    ctx.fillStyle = k > 0.6 ? '#b6f07a' : '#7fd84a';
    ctx.globalAlpha = 0.45 + 0.55 * k;
    oval(
      ctx,
      m.x + m.nx * side * w * (0.8 + 0.4 * k),
      m.y + m.ny * side * w * (0.8 + 0.4 * k),
      c * 0.17 * (0.5 + 0.7 * k),
      c * 0.075 * (0.5 + 0.7 * k),
      rot,
    );
  }
};

const fxMagnet: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  const ph = fract(t * 0.55);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(3, c * 0.08);
  glow(ctx, light, c * 0.3);
  for (const from of [0, 1]) {
    const s = ph * (g.total / 2);
    const m = g.at(from === 0 ? s : g.total - s, false);
    ctx.globalAlpha = 1 - ph * 0.5;
    ring(ctx, m.x, m.y, w * (0.7 + ph * 1.1));
    ctx.fillStyle = '#ffffff';
    disc(ctx, m.x, m.y, w * 0.4);
  }
  if (ph > 0.88) {
    const m = g.at(g.total / 2, false);
    ctx.globalAlpha = (ph - 0.88) * 8;
    ctx.fillStyle = '#ffffff';
    disc(ctx, m.x, m.y, w * 1.1);
  }
};

const fxCandy: FxFn = (e) => {
  stripes(e, e.c * 0.3, -e.c * 1.2, -0.7, '#ffffff', Math.max(3, e.c * 0.09), 0.8, 'round');
};

const fxBone: FxFn = (e) => {
  const { ctx, g, c, t } = e;
  ctx.fillStyle = '#f7f1dc';
  glow(ctx, '#ffffff', c * 0.15);
  const n = Math.floor(g.total / (c * 0.45));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.45, false);
    const hop = Math.abs(Math.sin(t * 6 - i * 0.8));
    ctx.globalAlpha = 0.9;
    disc(ctx, m.x, m.y - hop * c * 0.2, c * 0.065);
  }
};

const fxMagic: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  const palette = ['#ffffff', '#ffe58a', light];
  glow(ctx, light, c * 0.3);
  for (let j = 0; j < 3; j++) {
    const head = (t * c * (1.6 + j * 0.5) + j * g.total * 0.33) % (g.total + c);
    for (let k = 0; k < 7; k++) {
      const s = head - k * c * 0.1;
      if (s < 0 || s > g.total) continue;
      const m = g.at(s, false);
      const o = Math.sin(t * 7 + j * 2 - k * 0.5) * w * 1.1;
      ctx.fillStyle = palette[j]!;
      ctx.globalAlpha = (1 - k / 7) * 0.95;
      star(ctx, m.x + m.nx * o, m.y + m.ny * o, c * 0.14 * (1 - k / 10));
    }
  }
};

const fxCyberpunk: FxFn = (e) => {
  const { ctx, g, c, w, t, seedInt } = e;
  const len = c * 0.9;
  const cyc = fract(t * 0.45);
  const pp = cyc < 0.5 ? cyc * 2 : 2 - cyc * 2;
  const head = len + pp * (g.total - len);
  const glitch = rnd(seedInt + Math.floor(t * 14)) > 0.82;
  ctx.lineCap = 'butt';
  const draw = (color: string, shift: number): void => {
    ctx.strokeStyle = color;
    ctx.lineWidth = w * 1.7;
    ctx.beginPath();
    for (let u = 0; u <= len; u += len / 8) {
      const m = g.at(head - len + u + shift, false);
      if (u === 0) ctx.moveTo(m.x, m.y);
      else ctx.lineTo(m.x, m.y);
    }
    ctx.stroke();
  };
  glow(ctx, '#38f4ff', c * 0.4);
  ctx.globalAlpha = 0.85;
  draw('#8afcff', 0);
  if (glitch) {
    ctx.globalAlpha = 0.7;
    draw('#ff3cf2', c * 0.22);
  }
};

const fxGoth: FxFn = (e) => {
  const { ctx, g, c, w, t, seedInt } = e;
  tracePts(ctx, g.pts);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#d3122f';
  glow(ctx, '#ff1c3c', c * 0.3);
  ctx.globalAlpha = 0.22 + 0.28 * (0.5 + 0.5 * Math.sin(t * 2.4));
  ctx.lineWidth = w * 1.4;
  ctx.stroke();
  ctx.fillStyle = '#b3102a';
  const n = slots(e, 1.1);
  for (let i = 0; i < n; i++) {
    const life = fract(t * 0.42 + rnd(seedInt + i * 5));
    const m = g.at(rnd(seedInt + i) * g.total, false);
    const y = m.y + w * 0.6 + life * life * c * 0.9;
    ctx.globalAlpha = 1 - life * 0.6;
    oval(ctx, m.x, y, c * 0.045, c * (0.06 + 0.06 * life), 0);
  }
};

const fxRobot: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  const gap = c * 1.15;
  const blink = Math.sin(t * 9) > -0.3 ? 1 : 0.35;
  glow(ctx, e.light, c * 0.2);
  for (let i = 0; i < Math.ceil(g.total / gap) + 1; i++) {
    const s = (t * c * 2.6 + i * gap) % (g.total + gap);
    if (s > g.total) continue;
    const m = g.at(s, false);
    ctx.save();
    ctx.translate(m.x, m.y);
    ctx.rotate(Math.atan2(m.ty, m.tx));
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = 0.9 * blink;
    ctx.fillRect(-c * 0.14, -w * 0.6, c * 0.28, w * 1.2);
    ctx.restore();
  }
};

const fxWheel: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(2, c * 0.045);
  ctx.lineCap = 'round';
  const n = Math.floor(g.total / (c * 0.5));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.5, false);
    const r = w * 0.8;
    ctx.globalAlpha = 0.8;
    for (let k = 0; k < 3; k++) {
      const a = t * 5 + (k * TAU) / 3;
      ctx.beginPath();
      ctx.moveTo(m.x, m.y);
      ctx.lineTo(m.x + Math.cos(a) * r, m.y + Math.sin(a) * r);
      ctx.stroke();
    }
  }
};

const fxEmo: FxFn = (e) => {
  const { ctx, g, c, w, t, light } = e;
  const ph = fract(t / 1.15);
  const b = Math.min(1, bump(ph, 0.05, 0.06) + 0.75 * bump(ph, 0.27, 0.06));
  tracePts(ctx, g.pts);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = light;
  glow(ctx, light, c * (0.15 + b * 0.5));
  ctx.globalAlpha = 0.12 + 0.75 * b;
  ctx.lineWidth = w * (1.1 + b * 1.1);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.globalAlpha = 0.15 + 0.6 * b;
  ctx.lineWidth = w * 0.35;
  ctx.stroke();
};

const fxArmor: FxFn = (e) => {
  const { g, c, t } = e;
  const head = (fract(t / 2.3) * 1.4 - 0.1) * g.total + c;
  glint(e, head, c * 1.2, '#ffffff', 0.85, 0.9, Math.max(3, c * 0.09));
};

const fxChrome: FxFn = (e) => {
  const { g, c, t } = e;
  const cyc = fract(t / 1.5);
  const h1 = g.total - (cyc * 1.3 - 0.1) * g.total;
  const h2 = g.total - (fract(cyc + 0.45) * 1.3 - 0.1) * g.total;
  glint(e, h1 + c * 0.6, c * 0.7, '#ffffff', 0.95, 0.5, Math.max(2.5, c * 0.07));
  glint(e, h2 + c * 0.6, c * 0.5, '#bfe9ff', 0.8, 0.5, Math.max(2, c * 0.05));
};

const fxViking: FxFn = (e) => {
  const { ctx, g, c, w, t } = e;
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(2, c * 0.05);
  const n = Math.floor(g.total / (c * 0.7));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.7, false);
    const b = Math.max(0, Math.sin(i * 0.8 - t * 4));
    ctx.strokeStyle = '#ffd27a';
    glow(ctx, '#ffb02e', c * 0.3 * b);
    ctx.globalAlpha = 0.15 + 0.85 * b;
    const r = w * 0.8;
    // simple angular rune: a spine with two slanted branches
    const sx = m.nx;
    const sy = m.ny;
    ctx.beginPath();
    ctx.moveTo(m.x - sx * r, m.y - sy * r);
    ctx.lineTo(m.x + sx * r, m.y + sy * r);
    ctx.moveTo(m.x, m.y - sy * r * 0.1);
    ctx.lineTo(m.x + m.tx * r * 0.7 + sx * r * 0.7, m.y + m.ty * r * 0.7 + sy * r * 0.7);
    ctx.lineTo(m.x + m.tx * r * 0.7 - sx * r * 0.2 + sx * r * 0.2, m.y + m.ty * r * 0.7 - sy * r * 0.2 + sy * r * 0.2);
    ctx.stroke();
  }
};

const fxSamurai: FxFn = (e) => {
  const { ctx, g, c, t, seedInt } = e;
  // drifting petals
  ctx.fillStyle = '#ffb7c5';
  const n = slots(e, 1.2);
  for (let i = 0; i < n; i++) {
    const life = fract(t * 0.35 + rnd(seedInt + i * 5));
    const m = g.at(rnd(seedInt + i) * g.total, false);
    const x = m.x + Math.sin(life * 7 + i) * c * 0.2 + life * c * 0.2;
    const y = m.y + life * c * 0.6 - c * 0.1;
    ctx.globalAlpha = Math.sin(Math.PI * life) * 0.95;
    oval(ctx, x, y, c * 0.065, c * 0.035, life * 6 + i);
  }
  // quick katana slash
  const period = 2.4;
  const k = Math.floor(t / period);
  const lt = (t % period) / 0.22;
  if (lt < 1) {
    const m = g.at(rnd(seedInt + k * 17) * g.total, false);
    const len = c * 0.9;
    ctx.strokeStyle = '#ffffff';
    ctx.lineCap = 'round';
    glow(ctx, '#ffffff', c * 0.25);
    ctx.globalAlpha = 1 - lt;
    ctx.lineWidth = Math.max(1.5, c * 0.04) * (1 - lt * 0.5);
    ctx.beginPath();
    ctx.moveTo(m.x - len * 0.5 * lt - len * 0.1, m.y + len * 0.5 * lt + len * 0.1);
    ctx.lineTo(m.x + len * 0.5 * lt + len * 0.1, m.y - len * 0.5 * lt - len * 0.1);
    ctx.stroke();
  }
};

const fxZombie: FxFn = (e) => {
  const { ctx, g, c, w, t, seedInt } = e;
  ctx.fillStyle = '#9bd63a';
  glow(ctx, '#5a8f12', c * 0.15);
  const n = Math.floor(g.total / (c * 0.55));
  for (let i = 0; i <= n; i++) {
    const m = g.at(i * c * 0.55, false);
    const side = rnd(seedInt + i) > 0.5 ? 1 : -1;
    const k = 0.5 + 0.5 * Math.sin(t * 2.2 + i * 1.7);
    ctx.globalAlpha = 0.35 + 0.55 * k;
    disc(ctx, m.x + m.nx * side * w * 0.55, m.y + m.ny * side * w * 0.55, w * (0.25 + 0.45 * k));
  }
  const drips = slots(e, 0.7);
  for (let i = 0; i < drips; i++) {
    const life = fract(t * 0.3 + rnd(seedInt + i * 3));
    const m = g.at(rnd(seedInt + i * 11) * g.total, false);
    ctx.globalAlpha = (1 - life) * 0.9;
    oval(ctx, m.x, m.y + w * 0.5 + life * life * c * 0.55, c * 0.04, c * (0.05 + 0.07 * life), 0);
  }
};

const fxToxic: FxFn = (e) => {
  const { ctx, g, c, w, t, seedInt } = e;
  ctx.strokeStyle = '#b6ff3a';
  ctx.lineWidth = Math.max(2, c * 0.05);
  glow(ctx, '#b6ff3a', c * 0.3);
  const n = Math.max(2, Math.round(g.total / (c * 1.1)));
  for (let i = 0; i < n; i++) {
    const ph = fract(t * 0.6 + rnd(seedInt + i * 5));
    const m = g.at(((i + 0.5) / n) * g.total, false);
    ctx.globalAlpha = (1 - ph) * 0.95;
    ring(ctx, m.x, m.y, w * (0.4 + ph * 1.5));
  }
  ctx.fillStyle = '#d8ff7a';
  const bubbles = slots(e, 1.2);
  for (let i = 0; i < bubbles; i++) {
    const life = fract(t * 0.55 + rnd(seedInt + i * 7));
    const m = g.at(rnd(seedInt + i * 13) * g.total, false);
    ctx.globalAlpha = (1 - life) * 0.9;
    disc(ctx, m.x + Math.sin(life * 8 + i) * c * 0.05, m.y - life * c * 0.6, c * 0.04);
  }
};

const fxDemon: FxFn = (e) => {
  const { ctx, g, c, w, t, seedInt } = e;
  tracePts(ctx, g.pts);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#ff2a1a';
  glow(ctx, '#ff1a00', c * 0.35);
  ctx.globalAlpha = 0.2 + 0.25 * (0.5 + 0.5 * Math.sin(t * 3.2));
  ctx.lineWidth = w * 1.5;
  ctx.stroke();
  const n = Math.max(2, Math.round(g.total / (c * 1.4)));
  for (let i = 0; i < n; i++) {
    const open = fract(t * 0.5 + rnd(seedInt + i * 5)) < 0.7;
    if (!open) continue;
    const m = g.at(((i + 0.5) / n) * g.total, false);
    const rot = Math.atan2(m.ty, m.tx);
    ctx.fillStyle = '#ffd21a';
    glow(ctx, '#ff3a00', c * 0.2);
    ctx.globalAlpha = 0.95;
    for (const d of [-1, 1]) {
      oval(
        ctx,
        m.x + m.tx * d * w * 0.5,
        m.y + m.ty * d * w * 0.5,
        w * 0.32,
        w * 0.12,
        rot + d * 0.45,
      );
    }
  }
};

const PATH_FX: Partial<Record<VisualStyle, FxFn>> = {
  pipe: fxPipe,
  snake: fxSnake,
  chain: fxChain,
  rope: fxRope,
  wave: fxWave,
  crystal: fxCrystal,
  lightning: fxLightning,
  spring: fxSpring,
  thread: fxThread,
  bubble: fxBubble,
  ribbon: fxRibbon,
  fire: fxFire,
  vine: fxVine,
  magnet: fxMagnet,
  candy: fxCandy,
  bone: fxBone,
  magic: fxMagic,
  cyberpunk: fxCyberpunk,
  goth: fxGoth,
  robot: fxRobot,
  wheel: fxWheel,
  emo: fxEmo,
  armor: fxArmor,
  chrome: fxChrome,
  viking: fxViking,
  samurai: fxSamurai,
  zombie: fxZombie,
  toxic: fxToxic,
  demon: fxDemon,
};

/** Continuous motion drawn over a finished path, specific to the visual style. */
export function drawCompletedPathFx(
  ctx: CanvasRenderingContext2D,
  style: VisualStyle,
  pts: Pt[],
  cell: number,
  base: string,
  light: string,
  nowMs: number,
  tubeRatio: number,
): void {
  if (pts.length < 2) return;
  const g = new PathGeom(pts);
  const first = pts[0]!;
  const seed = fract(Math.sin(first.x * 0.0731 + first.y * 0.1337) * 4375.5453);
  const env: Env = {
    ctx,
    g,
    c: cell,
    w: (cell * tubeRatio) / 2,
    t: nowMs / 1000 + seed * 10,
    base,
    light,
    seedInt: Math.floor(seed * 1000),
  };
  ctx.save();
  try {
    (PATH_FX[style] ?? fxPipe)(env);
  } finally {
    ctx.restore();
  }
}

type DotFxKind = 'ripple' | 'square' | 'diamond' | 'orbit' | 'rays' | 'rise' | 'beat';

const DOT_FX_KIND: Record<VisualStyle, DotFxKind> = {
  pipe: 'ripple',
  snake: 'beat',
  gear: 'ripple',
  chain: 'orbit',
  rope: 'orbit',
  wave: 'ripple',
  crystal: 'diamond',
  lightning: 'rays',
  spring: 'beat',
  thread: 'orbit',
  bubble: 'rise',
  ribbon: 'ripple',
  fire: 'rays',
  vine: 'rise',
  magnet: 'ripple',
  candy: 'orbit',
  bone: 'beat',
  magic: 'orbit',
  cyberpunk: 'square',
  goth: 'beat',
  robot: 'square',
  wheel: 'orbit',
  emo: 'beat',
  armor: 'square',
  chrome: 'diamond',
  viking: 'orbit',
  samurai: 'rays',
  zombie: 'rise',
  toxic: 'ripple',
  demon: 'rays',
};

/** Style-specific halo around a connected pair's endpoint dot (drawn behind it). */
export function drawCompletedDotFx(
  ctx: CanvasRenderingContext2D,
  style: VisualStyle,
  x: number,
  y: number,
  radius: number,
  light: string,
  nowMs: number,
  seed: number,
): void {
  const t = nowMs / 1000 + seed * 3;
  ctx.save();
  ctx.strokeStyle = light;
  ctx.fillStyle = light;
  ctx.lineWidth = Math.max(1.5, radius * 0.12);
  glow(ctx, light, radius * 0.3);
  switch (DOT_FX_KIND[style]) {
    case 'ripple':
      for (let k = 0; k < 2; k++) {
        const ph = fract(t * 0.7 + k * 0.5);
        ctx.globalAlpha = (1 - ph) * 0.85;
        ring(ctx, x, y, radius * (1.02 + ph * 0.28));
      }
      break;
    case 'square':
      for (let k = 0; k < 2; k++) {
        const ph = fract(t * 0.7 + k * 0.5);
        const r = radius * (1 + ph * 0.22);
        ctx.globalAlpha = (1 - ph) * 0.85;
        ctx.strokeRect(x - r, y - r, r * 2, r * 2);
      }
      break;
    case 'diamond':
      for (let k = 0; k < 2; k++) {
        const ph = fract(t * 0.7 + k * 0.5);
        const r = radius * (1.05 + ph * 0.22);
        ctx.globalAlpha = (1 - ph) * 0.85;
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x, y + r);
        ctx.lineTo(x - r, y);
        ctx.closePath();
        ctx.stroke();
      }
      break;
    case 'orbit': {
      const r = radius * 1.18;
      ctx.globalAlpha = 0.35;
      ring(ctx, x, y, r);
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#ffffff';
      for (let k = 0; k < 3; k++) {
        const a = t * 3.2 + (k * TAU) / 3;
        disc(ctx, x + Math.cos(a) * r, y + Math.sin(a) * r, radius * 0.17);
      }
      break;
    }
    case 'rays': {
      const flick = 0.6 + 0.4 * Math.sin(t * 17) * Math.sin(t * 11);
      for (let k = 0; k < 8; k++) {
        const a = (k * TAU) / 8 + t * 0.8;
        const len = radius * (0.12 + 0.16 * rnd(k + Math.floor(t * 6) * 8));
        ctx.globalAlpha = 0.9 * flick;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * radius * 1.05, y + Math.sin(a) * radius * 1.05);
        ctx.lineTo(x + Math.cos(a) * (radius * 1.05 + len), y + Math.sin(a) * (radius * 1.05 + len));
        ctx.stroke();
      }
      break;
    }
    case 'rise':
      for (let k = 0; k < 4; k++) {
        const life = fract(t * 0.5 + k * 0.25);
        ctx.globalAlpha = (1 - life) * 0.9;
        disc(
          ctx,
          x + Math.sin(life * 8 + k * 2) * radius * 0.5,
          y - radius * 0.5 - life * radius * 0.7,
          radius * (0.08 + 0.07 * rnd(k)),
        );
      }
      break;
    case 'beat': {
      const ph = fract(t / 1.1);
      const b = Math.min(1, bump(ph, 0.05, 0.07) + 0.7 * bump(ph, 0.26, 0.07));
      ctx.globalAlpha = 0.25 + 0.7 * b;
      ctx.lineWidth = Math.max(2, radius * (0.12 + 0.1 * b));
      ring(ctx, x, y, radius * (1.03 + 0.14 * b));
      break;
    }
  }
  ctx.restore();
}
